import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useChartData } from '../hooks/useChartData';
import type { SiteTimeSeries } from '../types';

interface UptimeChartProps {
    timeSeries: SiteTimeSeries[];
    isLoading: boolean;
}

export const UptimeChart: React.FC<UptimeChartProps> = ({ timeSeries, isLoading }) => {
    const { labels, datasets, minMax } = useChartData(timeSeries, 'uptime');
    const svgRef = useRef<SVGSVGElement>(null);
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
    const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

    if (isLoading || datasets.length === 0) {
        return (
            <div className="mx-[18px] h-[200px] bg-[var(--bg-elevated)] border border-[var(--border)] rounded-xl animate-pulse" />
        );
    }

    const width = 800;
    const height = 200;
    const padding = { top: 20, right: 20, bottom: 40, left: 50 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;

    const yScale = (val: number) => 
        padding.top + chartHeight - ((val - minMax.min) / (minMax.max - minMax.min)) * chartHeight;
    
    const xScale = (index: number) => 
        padding.left + (index / (labels.length - 1)) * chartWidth;

    const handleMouseMove = (e: React.MouseEvent) => {
        if (!svgRef.current) return;
        const rect = svgRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const index = Math.round(((x - padding.left) / chartWidth) * (labels.length - 1));
        setHoveredIndex(Math.max(0, Math.min(labels.length - 1, index)));
        setTooltipPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="mx-[18px] bg-[var(--bg-elevated)] border border-[var(--border)] rounded-xl p-4"
        >
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-[13px] font-medium text-[var(--text)]">Динамика доступности</h3>
                <div className="flex items-center gap-3 text-[11px]">
                    {datasets.map(ds => (
                        <span key={ds.label} className="flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full" style={{ background: ds.color }} />
                            <span className="text-[var(--text-secondary)]">{ds.label}</span>
                        </span>
                    ))}
                </div>
            </div>

            <div className="relative">
                <svg
                    ref={svgRef}
                    viewBox={`0 0 ${width} ${height}`}
                    className="w-full h-[200px] overflow-visible"
                    onMouseMove={handleMouseMove}
                    onMouseLeave={() => setHoveredIndex(null)}
                >
                    {[0, 0.25, 0.5, 0.75, 1].map(t => {
                        const y = padding.top + t * chartHeight;
                        const value = minMax.max - t * (minMax.max - minMax.min);
                        return (
                            <g key={t}>
                                <line
                                    x1={padding.left}
                                    y1={y}
                                    x2={width - padding.right}
                                    y2={y}
                                    stroke="var(--border)"
                                    strokeDasharray="4 4"
                                />
                                <text
                                    x={padding.left - 8}
                                    y={y + 4}
                                    textAnchor="end"
                                    className="fill-[var(--text-muted)] text-[10px]"
                                >
                                    {value.toFixed(1)}%
                                </text>
                            </g>
                        );
                    })}

                    {datasets.map((ds, di) => {
                        const points = ds.data.map((d, i) => `${xScale(i)},${yScale(d)}`).join(' ');
                        
                        const areaPoints = `${padding.left},${padding.top + chartHeight} ${points} ${width - padding.right},${padding.top + chartHeight}`;
                        
                        return (
                            <g key={ds.label}>
                                <motion.path
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 0.1 }}
                                    d={`M${areaPoints}Z`}
                                    fill={ds.color}
                                />
                                
                                <motion.polyline
                                    initial={{ pathLength: 0 }}
                                    animate={{ pathLength: 1 }}
                                    transition={{ duration: 1, delay: di * 0.1 }}
                                    points={points}
                                    fill="none"
                                    stroke={ds.color}
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                
                                {hoveredIndex !== null && (
                                    <circle
                                        cx={xScale(hoveredIndex)}
                                        cy={yScale(ds.data[hoveredIndex])}
                                        r="4"
                                        fill={ds.color}
                                        stroke="var(--bg-elevated)"
                                        strokeWidth="2"
                                    />
                                )}
                            </g>
                        );
                    })}

                    {labels.filter((_, i) => i % Math.ceil(labels.length / 6) === 0).map((label, i, arr) => {
                        const index = i * Math.ceil(labels.length / 6);
                        return (
                            <text
                                key={i}
                                x={xScale(index)}
                                y={height - 10}
                                textAnchor={i === 0 ? 'start' : i === arr.length - 1 ? 'end' : 'middle'}
                                className="fill-[var(--text-muted)] text-[10px]"
                            >
                                {label.replace('\n', ' ')}
                            </text>
                        );
                    })}

                    {hoveredIndex !== null && (
                        <line
                            x1={xScale(hoveredIndex)}
                            y1={padding.top}
                            x2={xScale(hoveredIndex)}
                            y2={padding.top + chartHeight}
                            stroke="var(--brand)"
                            strokeWidth="1"
                            strokeDasharray="4 4"
                            opacity="0.5"
                        />
                    )}
                </svg>

                {hoveredIndex !== null && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="absolute pointer-events-none bg-[var(--bg-elevated)] border border-[var(--border)] rounded-lg p-2 shadow-lg z-10"
                        style={{ 
                            left: Math.min(tooltipPos.x + 10, width - 150), 
                            top: Math.max(tooltipPos.y - 60, 0) 
                        }}
                    >
                        <div className="text-[10px] text-[var(--text-muted)] mb-1">
                            {labels[hoveredIndex]?.replace('\n', ' ')}
                        </div>
                        {datasets.map(ds => (
                            <div key={ds.label} className="flex items-center gap-2 text-[12px]">
                                <span className="w-2 h-2 rounded-full" style={{ background: ds.color }} />
                                <span className="text-[var(--text-secondary)]">{ds.label}:</span>
                                <span className="font-mono font-medium text-[var(--text)]">
                                    {ds.data[hoveredIndex].toFixed(2)}%
                                </span>
                            </div>
                        ))}
                    </motion.div>
                )}
            </div>
        </motion.div>
    );
};